import { useState, useEffect } from 'react';
import { getUserInfo, AvailableModel, KnowledgeBase } from '../services/ai/fastApiBackend';
import { getUserIdentity } from '../services/identity';
import { createOfficeContext } from '../services/office/factory';
import { IOfficeContext, InitResult, HostType } from '../types/office';

export function useOfficeInitialization(): InitResult {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLicenseError, setIsLicenseError] = useState<boolean>(false);
  const [requiresPopup, setRequiresPopup] = useState<boolean>(false);
  const [loginHint, setLoginHint] = useState<string | null>(null);
  const [context, setContext] = useState<IOfficeContext | null>(null);
  const [item, setItem] = useState<Office.MessageRead | Office.MessageCompose | null>(null);
  const [mode, setMode] = useState<"read" | "compose" | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [senderName, setSenderName] = useState<string>("");
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      console.log("[Office Init] Starting initialization...");
      
      try {
        // 1. Wait for Office
        console.log("[Office Init] Step 1: Waiting for Office.onReady()...");
        await Office.onReady();
        console.log("[Office Init] Step 1: Office.onReady() completed");
        
        const host = Office.context.host as HostType; // e.g., "Outlook", "Word"
        console.log(`[Office Init] Step 1: Office Ready - Host detected: ${host}`);
        console.log(`[Office Init] Step 1: Office.context available:`, !!Office.context);
        console.log(`[Office Init] Step 1: Office.context.host value:`, Office.context.host);
        console.log(`[Office Init] Step 1: Office.context.host type:`, typeof Office.context.host);

        // 2. Get Identity (Common across all apps)
        console.log("[Office Init] Step 2: Getting user identity...");
        let identityResult;
        try {
          // Try silent authentication first (don't auto-trigger popup)
          identityResult = await getUserIdentity(false);
          console.log("[Office Init] Step 2: Identity retrieved:", {
            hasIdentity: !!identityResult.identity,
            hasEmail: !!identityResult.identity?.email,
            hasDisplayName: !!identityResult.identity?.displayName,
            email: identityResult.identity?.email || "N/A",
            displayName: identityResult.identity?.displayName || "N/A",
            requiresPopup: identityResult.requiresPopup
          });

          console.log("Access token:", identityResult.identity?.rawAccessToken);
          
          // If popup is required, set state and return early
          if (identityResult.requiresPopup) {
            console.log("[Office Init] Step 2: Popup authentication required");
            if (isMounted) {
              setRequiresPopup(true);
              setLoginHint(identityResult.loginHint || null);
            }
            return; // Stop initialization, wait for user to click login button
          }
        } catch (identityError) {
          console.error("[Office Init] Step 2: Error getting identity:", identityError);
          throw identityError;
        }
        
        const identity = identityResult.identity;
        const email = identity?.email || "";
        let name = identity?.displayName || "";
        const hasToken = !!identity?.rawAccessToken;

        // For personal accounts, tokens are opaque and cannot be JWT decoded
        // If we have a token but no email, it's likely a personal account - continue anyway
        // The backend will handle authentication with the opaque token
        if (!email && !hasToken) {
          console.warn("[Office Init] Step 2: Could not determine user identity - no email and no token found");
          console.warn("[Office Init] Step 2: Identity object:", identity);
          if (isMounted) setIsLicenseError(true);
          return;
        }

        // If we have a token but no email (opaque token for personal account), log and continue
        if (hasToken && !email) {
          console.log("[Office Init] Step 2: Opaque token detected (personal account) - no email from JWT decode, but token available");
          console.log("[Office Init] Step 2: Backend will handle authentication with opaque token");
        }

        console.log("[Office Init] Step 2: User identity resolved:", { email, name });
        console.log("[Office Init] Step 2: raw access token:", identity?.rawAccessToken);

        // 3. Auth with Backend (Common across all apps)
        console.log("[Office Init] Step 3: Authenticating with backend...");
        try {
          // Get SSO token if not already available from identity
          let token = identity?.rawAccessToken;
          if (!token) {
            // Fallback: Try OfficeRuntime.auth.getAccessToken if MSAL NAA didn't provide a token
            // This should rarely happen as all hosts now use MSAL NAA when supported
            if (typeof OfficeRuntime !== 'undefined' && OfficeRuntime?.auth?.getAccessToken) {
              console.log("[Office Init] Step 3: Attempting fallback token retrieval via OfficeRuntime");
              token = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true });
            } else {
              throw new Error('SSO token not available and cannot be retrieved. Ensure MSAL NAA is properly configured.');
            }
          }

          const userInfo = await getUserInfo(token);
          console.log("[Office Init] Step 3: Auth successful, user info retrieved:", {
            hasUserInfo: !!userInfo,
            userName: userInfo?.user?.name,
            licenseActive: userInfo?.license?.active,
            availableModelsCount: userInfo?.available_models?.length || 0,
            knowledgeBasesCount: userInfo?.knowledge_bases?.length || 0
          });
          
          // For personal accounts with opaque tokens, use backend user.name as fallback
          // The identity.displayName will be empty because JWT can't be decoded
          if (!name && userInfo?.user?.name) {
            name = userInfo.user.name;
            console.log("[Office Init] Step 3: Using user name from backend response:", name);
          }
          
          // Check if license is active - if not, set license error but continue initialization
          if (!userInfo.license?.active) {
            console.warn("[Office Init] Step 3: License is not active - allowing UI to load with disabled inputs");
            if (isMounted) {
              setIsLicenseError(true);
            }
          }
          
          // Store available models and knowledge bases
          if (isMounted) {
            setAvailableModels(userInfo.available_models || []);
            setKnowledgeBases(userInfo.knowledge_bases || []);
          }
        } catch (err) {
          console.error("[Office Init] Step 3: Auth Failed", err);
          console.error("[Office Init] Step 3: Auth error details:", {
            error: err,
            errorMessage: err instanceof Error ? err.message : String(err),
            errorStack: err instanceof Error ? err.stack : undefined
          });
          if (isMounted) setIsLicenseError(true);
          return; // Stop if auth fails
        }

        // 4. Extract host-specific properties
        console.log("[Office Init] Step 4: Extracting host-specific properties...");
        let currentItem: Office.MessageRead | Office.MessageCompose | null = null;
        let currentMode: "read" | "compose" | null = null;
        let currentSenderName = "";

        // Outlook-specific extraction: item, mode, and sender name
        if (host === "Outlook") {
          console.log("[Office Init] Step 4: Outlook detected, extracting Outlook-specific properties...");
          console.log("[Office Init] Step 4: Office.context.mailbox available:", !!Office.context.mailbox);
          
          if (Office.context.mailbox) {
            // Try to get the item - it may be null if multi-select or other scenarios
            currentItem = Office.context.mailbox.item;
            console.log("[Office Init] Step 4: Outlook item retrieved:", {
              hasItem: !!currentItem,
              itemType: currentItem?.itemType
            });
            
            // Determine mode
            if (currentItem) {
              // Compose items don't expose an itemId until saved, so presence of itemId implies read mode
              const hasItemId = "itemId" in currentItem && !!(currentItem as Office.MessageRead).itemId;
              currentMode = hasItemId ? "read" : "compose";
              console.log("[Office Init] Step 4: Outlook mode determined:", currentMode, { hasItemId });
              
              // Get sender name (only in read mode)
              if (currentMode === "read" && "from" in currentItem && currentItem.from) {
                console.log("[Office Init] Step 4: Extracting sender name from read item...");
                if (typeof currentItem.from === "string") {
                  currentSenderName = currentItem.from;
                } else if ("displayName" in currentItem.from && currentItem.from.displayName) {
                  currentSenderName = currentItem.from.displayName;
                } else if ("emailAddress" in currentItem.from && currentItem.from.emailAddress) {
                  currentSenderName = currentItem.from.emailAddress;
                }
                console.log("[Office Init] Step 4: Sender name extracted:", currentSenderName);
              }
            } else {
              console.warn("[Office Init] Step 4: No Outlook item found (may be multi-select or other scenario) - continuing with null item");
              // Continue anyway - chat will work with empty context
            }
          } else {
            console.warn("[Office Init] Step 4: Office.context.mailbox not available");
          }
        } 
        // Word-specific extraction: document context (handled by DocumentAdapter, no special extraction needed)
        else if (host === "Word") {
          console.log("[Office Init] Step 4: Word detected, using document context");
          console.log("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
          // Word uses document context - no item, mode, or senderName needed
          // All Word-specific logic is handled by DocumentAdapter
        }
        // PowerPoint-specific extraction: document context (handled by DocumentAdapter, no special extraction needed)
        else if (host === "PowerPoint") {
          console.log("[Office Init] Step 4: PowerPoint detected, using document context");
          console.log("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
          // PowerPoint uses document context - no item, mode, or senderName needed
          // All PowerPoint-specific logic is handled by DocumentAdapter
        }
        // Other hosts (Excel, etc.)
        else {
          console.log(`[Office Init] Step 4: Other host detected (${host}), using document context`);
          console.log("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
        }

        console.log("[Office Init] Step 4: Host-specific properties:", {
          host,
          item: currentItem ? "present" : "null",
          mode: currentMode,
          senderName: currentSenderName || "N/A"
        });

        // 5. Create Context Strategy (The specific logic)
        console.log("[Office Init] Step 5: Creating office context...");
        console.log("[Office Init] Step 5: Calling createOfficeContext with:", { host, email });
        let officeContext;
        try {
          officeContext = createOfficeContext(host, email);
          console.log("[Office Init] Step 5: Office context created:", {
            hasContext: !!officeContext,
            contextHost: officeContext?.host,
            contextMetadata: officeContext?.metadata
          });
        } catch (contextError) {
          console.error("[Office Init] Step 5: Error creating office context:", contextError);
          console.error("[Office Init] Step 5: Context error details:", {
            error: contextError,
            errorMessage: contextError instanceof Error ? contextError.message : String(contextError),
            errorStack: contextError instanceof Error ? contextError.stack : undefined,
            host,
            email
          });
          throw contextError;
        }
        
        if (isMounted) {
          console.log("[Office Init] Step 6: Updating state with initialization results...");
          setContext(officeContext);
          setItem(currentItem);
          setMode(currentMode);
          setUserName(name);
          setUserEmail(email);
          setSenderName(currentSenderName);
          setIsReady(true);
          console.log("[Office Init] Step 6: State updated, initialization complete");
          console.log("[Office Init] Final state:", {
            isReady: true,
            isLicenseError: false,
            hasContext: !!officeContext,
            item: currentItem ? "present" : "null",
            mode: currentMode,
            userName: name,
            userEmail: email,
            senderName: currentSenderName
          });
        } else {
          console.warn("[Office Init] Step 6: Component unmounted, skipping state update");
        }

      } catch (e) {
        console.error("[Office Init] Initialization failed with error:", e);
        console.error("[Office Init] Error details:", {
          error: e,
          errorMessage: e instanceof Error ? e.message : String(e),
          errorStack: e instanceof Error ? e.stack : undefined,
          errorName: e instanceof Error ? e.name : typeof e
        });
        if (isMounted) {
          console.log("[Office Init] Setting isReady to true despite error");
          setIsReady(true); // Ready but failed
        }
      }
    };

    console.log("[Office Init] Starting initialization process...");
    initialize();

    return () => {
      console.log("[Office Init] Cleanup: Component unmounting");
      isMounted = false;
    };
  }, []);

  // Callback for when popup authentication succeeds
  const handlePopupSuccess = async (token: string) => {
    console.log("[Office Init] Popup authentication succeeded, retrying initialization...");
    setRequiresPopup(false);
    
    try {
      // Get user info with the new token
      const userInfo = await getUserInfo(token);
      
      // Check if license is active - if not, set license error but continue initialization
      if (!userInfo.license?.active) {
        console.warn("[Office Init] License is not active after popup auth - allowing UI to load with disabled inputs");
        setIsLicenseError(true);
      }
      
      // Get identity again to get full user info
      const identityResult = await getUserIdentity(true);
      const identity = identityResult.identity;
      const email = identity?.email || "";
      let name = identity?.displayName || "";
      const hasToken = !!identity?.rawAccessToken;
      
      // For personal accounts with opaque tokens, use backend user.name as fallback
      // The identity.displayName will be empty because JWT can't be decoded
      if (!name && userInfo?.user?.name) {
        name = userInfo.user.name;
        console.log("[Office Init] Using user name from backend response after popup auth:", name);
      }
      
      // For personal accounts, tokens are opaque and cannot be JWT decoded
      // If we have a token but no email, it's likely a personal account - continue anyway
      // The backend will handle authentication with the opaque token
      if (!email && !hasToken) {
        console.error("[Office Init] Could not get user identity after popup auth - no email and no token");
        setIsLicenseError(true);
        return;
      }

      // If we have a token but no email (opaque token for personal account), log and continue
      if (hasToken && !email) {
        console.log("[Office Init] Opaque token detected after popup auth (personal account) - no email from JWT decode, but token available");
        console.log("[Office Init] Backend will handle authentication with opaque token");
      }
      
      // Get host and create context
      const host = Office.context.host as HostType;
      const officeContext = createOfficeContext(host, email);
      
      // Extract Outlook-specific properties if needed
      let currentItem: Office.MessageRead | Office.MessageCompose | null = null;
      let currentMode: "read" | "compose" | null = null;
      let currentSenderName = "";
      
      if (host === "Outlook" && Office.context.mailbox) {
        const mailboxItem = Office.context.mailbox.item;
        if (mailboxItem) {
          currentItem = mailboxItem as Office.MessageRead | Office.MessageCompose;
          currentMode = mailboxItem.itemType === Office.MailboxEnums.ItemType.Message ? "read" : "compose";
          
          if (currentMode === "read" && (mailboxItem as Office.MessageRead).from) {
            currentSenderName = (mailboxItem as Office.MessageRead).from?.displayName || "";
          }
        }
      }
      
      // Update state
      setAvailableModels(userInfo.available_models || []);
      setKnowledgeBases(userInfo.knowledge_bases || []);
      setContext(officeContext);
      setItem(currentItem);
      setMode(currentMode);
      setUserName(name);
      setUserEmail(email);
      setSenderName(currentSenderName);
      setIsReady(true);
      
      console.log("[Office Init] Initialization complete after popup auth");
    } catch (error) {
      console.error("[Office Init] Error completing initialization after popup auth:", error);
      setIsLicenseError(true);
    }
  };

  return { 
    isReady, 
    isLicenseError,
    requiresPopup,
    loginHint,
    context,
    item,
    mode,
    userName,
    userEmail,
    senderName,
    availableModels,
    knowledgeBases,
    onPopupSuccess: handlePopupSuccess
  };
}